import { useState, useEffect } from "react";
import apiClient from "@/lib/apiClient";

import { 
  BookOpen, 
  AlertTriangle, 
  CheckCircle,
  Search,
  ChevronDown,
  ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reference() {
  const [stoppCriteria, setStoppCriteria] = useState([]);
  const [startCriteria, setStartCriteria] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [openCategories, setOpenCategories] = useState({});

  useEffect(() => {
    fetchCriteria();
  }, []);

  const fetchCriteria = async () => {
    try {
      const [stoppRes, startRes] = await Promise.all([
        apiClient.get(`${API}/reference/stopp-criteria`),
        apiClient.get(`${API}/reference/start-criteria`)
      ]);
      setStoppCriteria(stoppRes.data);
      setStartCriteria(startRes.data);
    } catch (error) {
      console.error("Error fetching criteria:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const groupByCategory = (criteria) => {
    return criteria.reduce((acc, item) => {
      const category = item.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});
  };

  const filterCriteria = (criteria) => {
    if (!searchTerm) return criteria;
    const term = searchTerm.toLowerCase();
    return criteria.filter(item => 
      item.description?.toLowerCase().includes(term) ||
      item.code?.toLowerCase().includes(term) ||
      item.category?.toLowerCase().includes(term) ||
      item.medications?.some(m => m.toLowerCase().includes(term))
    );
  };

  const filteredStopp = filterCriteria(stoppCriteria);
  const filteredStart = filterCriteria(startCriteria);

  const groupedStopp = groupByCategory(filteredStopp);
  const groupedStart = groupByCategory(filteredStart);

  return (
    <div className="animate-fade-in" data-testid="reference-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 tracking-tight font-heading">
          Référentiel STOPP/START
        </h1>
        <p className="mt-2 text-slate-600">
          Critères de prescription potentiellement inappropriée chez le sujet âgé (version 2)
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Rechercher par mot-clé, médicament..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-criteria"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="stopp" className="space-y-6" data-testid="reference-tabs">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="stopp" className="data-[state=active]:bg-white" data-testid="tab-stopp-ref">
            <AlertTriangle className="mr-2 h-4 w-4 text-red-500" />
            STOPP ({filteredStopp.length})
          </TabsTrigger>
          <TabsTrigger value="start" className="data-[state=active]:bg-white" data-testid="tab-start-ref">
            <CheckCircle className="mr-2 h-4 w-4 text-emerald-500" />
            START ({filteredStart.length})
          </TabsTrigger>
        </TabsList>

        {/* STOPP Criteria */}
        <TabsContent value="stopp" data-testid="stopp-ref-content">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                Chargement...
              </CardContent>
            </Card>
          ) : Object.keys(groupedStopp).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-slate-500">Aucun critère trouvé</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedStopp).map(([category, items]) => (
                <Collapsible
                  key={category}
                  open={openCategories[`stopp-${category}`] !== false}
                  onOpenChange={() => toggleCategory(`stopp-${category}`)}
                >
                  <Card>
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                        <CardTitle className="flex items-center justify-between text-lg">
                          <span className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-red-500" />
                            {category}
                          </span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{items.length}</Badge>
                            {openCategories[`stopp-${category}`] !== false ? (
                              <ChevronDown className="h-5 w-5 text-slate-400" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-slate-400" />
                            )}
                          </div>
                        </CardTitle>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0 space-y-3">
                        {items.map((item, index) => (
                          <div
                            key={index}
                            className="p-4 bg-red-50 rounded-lg border border-red-100"
                            data-testid={`stopp-item-${item.code}`}
                          >
                            <div className="flex items-start gap-3">
                              <Badge className="badge-stopp flex-shrink-0">
                                {item.code}
                              </Badge>
                              <div>
                                <p className="text-slate-800 font-medium mb-2">
                                  {item.description}
                                </p>
                                {item.medications?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {item.medications.map((med, i) => (
                                      <Badge key={i} variant="outline" className="text-xs">
                                        {med}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          )}
        </TabsContent>

        {/* START Criteria */}
        <TabsContent value="start" data-testid="start-ref-content">
          {loading ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                Chargement...
              </CardContent>
            </Card>
          ) : Object.keys(groupedStart).length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-slate-500">Aucun critère trouvé</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Object.entries(groupedStart).map(([category, items]) => (
                <Collapsible
                  key={category}
                  open={openCategories[`start-${category}`] !== false}
                  onOpenChange={() => toggleCategory(`start-${category}`)}
                >
                  <Card>
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                        <CardTitle className="flex items-center justify-between text-lg">
                          <span className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-emerald-500" />
                            {category}
                          </span>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{items.length}</Badge>
                            {openCategories[`start-${category}`] !== false ? (
                              <ChevronDown className="h-5 w-5 text-slate-400" />
                            ) : (
                              <ChevronRight className="h-5 w-5 text-slate-400" />
                            )}
                          </div>
                        </CardTitle>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0 space-y-3">
                        {items.map((item, index) => (
                          <div
                            key={index}
                            className="p-4 bg-emerald-50 rounded-lg border border-emerald-100"
                            data-testid={`start-item-${item.code}`}
                          >
                            <div className="flex items-start gap-3">
                              <Badge className="badge-start flex-shrink-0">
                                {item.code}
                              </Badge>
                              <div>
                                <p className="text-slate-800 font-medium mb-2">
                                  {item.description}
                                </p>
                                {item.condition?.length > 0 && (
                                  <p className="text-sm text-slate-600 mb-2">
                                    <span className="font-medium">Condition:</span>{" "}
                                    {item.condition.join(", ")}
                                  </p>
                                )}
                                <div className="p-2 bg-white rounded border border-emerald-100">
                                  <p className="text-sm text-emerald-700">
                                    <span className="font-medium">Traitement recommandé:</span>{" "}
                                    {item.medication}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Footer info */}
      <Card className="mt-8">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <BookOpen className="h-5 w-5 text-slate-400 mt-0.5" />
            <div className="text-sm text-slate-600">
              <p className="font-medium text-slate-700 mb-1">À propos des critères STOPP/START</p>
              <p>
                Les critères STOPP (Screening Tool of Older Persons' Prescriptions) et START 
                (Screening Tool to Alert to Right Treatment) constituent un outil validé 
                pour l'optimisation des prescriptions médicamenteuses chez le sujet âgé. 
                Version 2 publiée en 2015.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
